import { StandardEvents } from '@shopify/events';

/**
 * <luna-buddy> — Luna keeps the shopper company while they scroll (mobile first).
 *
 * - Scroll buddy: hops out of the header logo once the shopper scrolls down and sits
 *   in the bottom-left corner, above the sticky add-to-cart bar. Leans while the page
 *   scrolls, lands with a hop, and jumps back into the logo near the top. While she
 *   is out, a moon holds her seat in the logo (snippets/luna-logo.liquid).
 * - Product page moments (each once per visit, at most one every few seconds, only
 *   when the target is fully on screen): points at the best-value pack, hops on the
 *   Buy it now button, peeks at the add-on, sparkles when a bigger pack saves more.
 *   At the bottom of a product page she points up: "Ready to order? Tap me." A tap
 *   takes the shopper back to the Buy it now button, where she hops on it.
 * - Reactions: jumps into the cart icon when something is added, then holds up the
 *   "1 more = 10% off" sign or dances when Buy 2 is unlocked. Three quick taps =
 *   secret dance. Lost on the 404 page.
 * - The ✕ puts her to sleep for a few days for that shopper: a small sleeping moon
 *   peeks from the screen edge (one tap wakes her), and near the top of the page she
 *   sleeps in the logo.
 *
 * She never blocks taps while doing a move (pointer-events off), stays out of the
 * way of drawers and dialogs, and is not shown to shoppers who prefer reduced motion.
 */

const NAP_KEY = 'luna-buddy:nap-until';
const DONE_KEY = 'luna-buddy:done';
const NAP_DAYS = 3;
const COOLDOWN_MS = 12000;
const FIRST_MOMENT_DELAY_MS = 2500;
const READY_MS = 10000;
const APPEAR_AT = 320;
const HOME_AT = 90;
const BOTTOM_BARS = ['.sticky-add-to-cart__bar', '#PBarNextFrameWrapper'];
const TAP_TARGETS = 'button, input, select, textarea, summary, [role="button"], a[href]';

/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
// Some in-app browsers pause frames for a moment; never wait on a frame forever.
const nextFrame = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => resolve(undefined));
    window.setTimeout(resolve, 100);
  });
/** @param {number} t */
const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
/** @param {number} value @param {number} min @param {number} max */
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const store = {
  /** @param {Storage} area @param {string} key */
  get(area, key) {
    try {
      return area.getItem(key);
    } catch {
      return null;
    }
  },
  /** @param {Storage} area @param {string} key @param {string} value */
  set(area, key, value) {
    try {
      area.setItem(key, value);
    } catch {
      // Storage can be blocked (private mode); Luna just forgets.
    }
  },
};

/** @param {Element | null} element */
const amountIn = (element) => {
  const match = element?.textContent?.match(/\d[\d,]*(\.\d+)?/);
  return match ? Number(match[0].replace(/,/g, '')) : 0;
};

class LunaBuddy extends HTMLElement {
  /** @type {Record<string, any>} */
  #config = {};
  /** @type {Record<string, string>} */
  #messages = {};
  /** @type {any} */
  #mascot = null;
  /** @type {HTMLElement | null} */
  #sprite = null;
  /** @type {HTMLButtonElement | null} */
  #napButton = null;
  /** @type {HTMLElement | null} */
  #bubble = null;
  /** @type {HTMLElement | null} */
  #text = null;
  /** @type {HTMLAnchorElement | null} */
  #link = null;

  #x = 0;
  #y = 0;
  #scale = 1;
  /** @type {'home' | 'out'} */
  #state = 'home';
  #busy = false;
  #started = false;
  #napping = false;
  #readyOn = false;
  #lastStunt = 0;
  #stayUntil = 0;
  #cartCount = 0;
  #lastScrollY = 0;
  #frame = 0;
  #pinFrame = 0;
  /** @type {(() => { x: number, y: number } | null) | null} */
  #pinPoint = null;
  /** @type {Set<string>} */
  #done = new Set();
  /** @type {number[]} */
  #taps = [];
  /** @type {number | undefined} */
  #scrollEndTimer;
  /** @type {number | undefined} */
  #idleTimer;
  /** @type {number | undefined} */
  #bubbleTimer;
  /** @type {number | undefined} */
  #settleTimer;
  /** @type {AbortController | null} */
  #listeners = null;
  /** @type {MutationObserver | null} */
  #savingsObserver = null;

  connectedCallback() {
    // Fixed positioning must not depend on the section wrapper, so live on <body>.
    if (this.parentElement !== document.body) {
      document.querySelectorAll('luna-buddy').forEach((other) => other !== this && other.remove());
      document.body.append(this);
      return;
    }
    if (this.#started) return;
    this.#started = true;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    try {
      this.#config = JSON.parse(this.querySelector('[data-luna-config]')?.textContent || '{}');
    } catch {
      this.#config = {};
    }
    this.#messages = this.#config.messages || {};
    this.#cartCount = Number(this.#config.cartCount) || 0;
    this.#mascot = this.querySelector('luna-mascot');
    this.#sprite = this.querySelector('.luna-buddy__sprite');
    this.#napButton = this.querySelector('.luna-buddy__nap');
    this.#bubble = this.querySelector('.luna-buddy__bubble');
    this.#text = this.querySelector('.luna-buddy__text');
    this.#link = this.querySelector('.luna-buddy__link');

    try {
      JSON.parse(store.get(sessionStorage, DONE_KEY) || '[]').forEach((/** @type {string} */ name) => this.#done.add(name));
    } catch {
      // Ignore broken session data.
    }

    this.#napping = Number(store.get(localStorage, NAP_KEY) || 0) > Date.now();
    if (this.#napping) this.#seatNap(true);

    this.#listen();
    this.#lastScrollY = window.scrollY;

    if (this.#config.template === '404' && !this.#napping) {
      this.#lost();
    } else if (window.scrollY > APPEAR_AT) {
      this.#appear();
    }
  }

  disconnectedCallback() {
    if (this.isConnected) return;
    this.#teardown();
    delete this.#seat(true)?.dataset.away;
  }

  /* ---------------------------------------------------------------- setup */

  #listen() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    window.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    window.addEventListener('resize', this.#onResize, { passive: true, signal });
    for (const type of ['pointerdown', 'keydown', 'touchstart', 'wheel']) {
      window.addEventListener(type, this.#resetIdle, { passive: true, signal });
    }
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { capture: true, signal });
    this.#sprite?.addEventListener('click', this.#onTap, { signal });
    this.#bubble?.addEventListener('click', this.#onBubbleTap, { signal });
    this.#napButton?.addEventListener('click', this.#onWake, { signal });
    this.querySelector('.luna-buddy__close')?.addEventListener('click', this.#onClose, { signal });

    const savings = document.querySelector('luna-savings');
    if (savings) {
      let previous = amountIn(savings);
      this.#savingsObserver = new MutationObserver(() => {
        const current = amountIn(savings);
        if (current > previous) this.#onBiggerSaving();
        previous = current;
      });
      this.#savingsObserver.observe(savings, { childList: true, subtree: true, characterData: true });
    }

    this.#resetIdle();
    window.setTimeout(() => this.#checkMoments(), FIRST_MOMENT_DELAY_MS + 200);
  }

  #teardown() {
    this.#listeners?.abort();
    this.#listeners = null;
    this.#savingsObserver?.disconnect();
    this.#savingsObserver = null;
    this.#unpin();
    window.clearTimeout(this.#scrollEndTimer);
    window.clearTimeout(this.#idleTimer);
    window.clearTimeout(this.#bubbleTimer);
    window.clearTimeout(this.#settleTimer);
  }

  /** @param {string} name */
  #markDone(name) {
    this.#done.add(name);
    store.set(sessionStorage, DONE_KEY, JSON.stringify([...this.#done]));
  }

  /* -------------------------------------------------------- positioning */

  #dims() {
    const w = parseFloat(getComputedStyle(this).getPropertyValue('--luna-w')) || 56;
    return { w, h: (w * 130) / 120 };
  }

  /** The Luna in the header logo, if it is on screen. */
  #seat(evenIfHidden = false) {
    /** @type {HTMLElement | null} */
    const seat = document.querySelector('.luna-logo luna-mascot');
    if (!seat || evenIfHidden) return seat;
    const rect = seat.getBoundingClientRect();
    return rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight ? seat : null;
  }

  /** @param {boolean} asleep Puts the Luna in the logo to sleep, or wakes her. */
  #seatNap(asleep) {
    this.#seat(true)?.toggleAttribute('data-nap', asleep);
  }

  #bottomInset() {
    let inset = 0;
    for (const selector of BOTTOM_BARS) {
      const element = document.querySelector(selector);
      if (!element) continue;
      const rect = element.getBoundingClientRect();
      if (rect.height > 0 && rect.top < window.innerHeight - 1) {
        inset = Math.max(inset, window.innerHeight - rect.top);
      }
    }
    return inset;
  }

  #topInset() {
    const header = document.querySelector('header');
    return header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
  }

  /** Her corner; while napping, the spot where the sleeping moon peeks from the edge. */
  #dock() {
    const { w } = this.#dims();
    const floor = window.innerHeight - this.#bottomInset();
    if (this.#napping) return { x: 12, y: floor - 22 };
    return { x: 12 + w / 2, y: floor - 8 };
  }

  /** Places Luna with her feet at (x, y) in the viewport. */
  #place(x = this.#x, y = this.#y, scale = this.#scale) {
    const { w, h } = this.#dims();
    this.#x = x;
    this.#y = y;
    this.#scale = scale;
    this.style.transform = `translate3d(${x - w / 2}px, ${y - h}px, 0) scale(${scale})`;
  }

  /**
   * Jumps along an arc to (x, y).
   * @param {number} x
   * @param {number} y
   * @param {{ duration?: number, arc?: number, scale?: number }} [options]
   */
  async #flyTo(x, y, { duration = 650, arc = 50, scale = 1 } = {}) {
    const { w, h } = this.#dims();
    const from = { x: this.#x, y: this.#y, scale: this.#scale };
    /** @type {Keyframe[]} */
    const frames = [];
    for (let i = 0; i <= 14; i += 1) {
      const t = i / 14;
      const e = ease(t);
      const fx = from.x + (x - from.x) * e;
      const fy = from.y + (y - from.y) * e - arc * 4 * t * (1 - t);
      const fs = from.scale + (scale - from.scale) * e;
      frames.push({ transform: `translate3d(${fx - w / 2}px, ${fy - h}px, 0) scale(${fs})` });
    }
    this.classList.remove('is-docked', 'is-tucked');
    const animation = this.animate(frames, { duration, easing: 'linear', fill: 'forwards' });
    try {
      // If the browser stops drawing frames (tab in the background), land anyway.
      await Promise.race([animation.finished, wait(duration + 400)]);
    } catch {
      // Cancelled; snap to the end.
    }
    this.#place(x, y, scale);
    animation.cancel();
  }

  /** @param {() => ({ x: number, y: number } | null)} point */
  #pinTo(point) {
    this.#pinPoint = point;
    const loop = () => {
      if (!this.#pinPoint) return;
      const next = this.#pinPoint();
      if (next) this.#place(next.x, next.y);
      this.#pinFrame = requestAnimationFrame(loop);
    };
    this.#pinFrame = requestAnimationFrame(loop);
  }

  #unpin() {
    this.#pinPoint = null;
    cancelAnimationFrame(this.#pinFrame);
  }

  /** @param {1 | -1} direction */
  #face(direction) {
    this.#sprite?.style.setProperty('--face', String(direction));
  }

  /**
   * @param {Element} element
   * @param {number} [headroom] Extra space needed above it (she stands on top of it).
   */
  #fullyVisible(element, headroom = 0) {
    const rect = element.getBoundingClientRect();
    return (
      rect.height > 0 &&
      rect.top >= this.#topInset() + 4 + headroom &&
      rect.bottom <= window.innerHeight - this.#bottomInset() - 4
    );
  }

  /* --------------------------------------------------------- expression */

  /** @param {string} name */
  async #play(name) {
    if (!this.#mascot?.play) return;
    if (this.#mascot.play(name, { interrupt: true })) return;
    // Just shown: the mascot may not know it is on screen yet.
    await wait(120);
    this.#mascot.play(name, { interrupt: true });
  }

  /** @param {string | null} pose */
  #pose(pose) {
    if (!this.#mascot) return;
    if (pose) {
      this.#mascot.dataset.pose = pose;
    } else {
      delete this.#mascot.dataset.pose;
    }
  }

  /**
   * @param {string | undefined} text
   * @param {{ ms?: number, link?: { href: string, label: string } | null, sign?: boolean, action?: boolean }} [options]
   */
  #say(text, { ms = 3000, link = null, sign = false, action = false } = {}) {
    if (!text || !this.#bubble || !this.#text || !this.#link) return;
    window.clearTimeout(this.#bubbleTimer);
    this.classList.remove('is-tucked');
    this.#text.textContent = text;
    if (link?.href && link.label) {
      this.#link.href = link.href;
      this.#link.textContent = link.label;
      this.#link.hidden = false;
    } else {
      this.#link.hidden = true;
    }
    this.#bubble.classList.toggle('has-link', !this.#link.hidden);
    this.#bubble.classList.toggle('is-sign', sign);
    this.#bubble.classList.toggle('is-action', action);
    this.dataset.side = this.#x > window.innerWidth / 2 ? 'left' : 'right';
    this.#bubble.hidden = false;
    requestAnimationFrame(() => this.#bubble?.classList.add('is-open'));
    this.#bubbleTimer = window.setTimeout(() => this.#hush(), ms);
  }

  #hush() {
    if (!this.#bubble) return;
    window.clearTimeout(this.#bubbleTimer);
    this.#bubble.classList.remove('is-open', 'is-action');
    if (this.#readyOn) {
      this.#readyOn = false;
      this.#pose(null);
    }
    window.setTimeout(() => {
      if (this.#bubble && !this.#bubble.classList.contains('is-open')) this.#bubble.hidden = true;
      this.#checkTuck();
    }, 200);
  }

  /**
   * In her corner, Luna must not take taps meant for a button, field or link under
   * her. When one is there, she tucks herself half off the screen edge.
   */
  #checkTuck() {
    if (this.#state !== 'out' || this.#busy || this.#napping || !this.classList.contains('is-docked')) return;
    if (this.#bubble && !this.#bubble.hidden) return;
    this.classList.toggle('is-tucked', this.#overTapTarget());
  }

  #overTapTarget() {
    const { w, h } = this.#dims();
    const left = this.#x - w / 2;
    const top = this.#y - h;
    for (const [fx, fy] of [[0.5, 0.5], [0.2, 0.3], [0.8, 0.3], [0.2, 0.85], [0.8, 0.85]]) {
      const x = left + w * fx;
      const y = top + h * fy;
      for (const element of document.elementsFromPoint(x, y)) {
        if (this.contains(element)) continue;
        const target = element.closest(TAP_TARGETS);
        if (target) {
          // A whole product card is one big link: plenty of it is left to tap.
          const rect = target.getBoundingClientRect();
          if (target.tagName !== 'A' || rect.width * rect.height < 200 * 90) return true;
        }
        break;
      }
    }
    return false;
  }

  #confetti() {
    const box = document.createElement('div');
    box.className = 'luna-confetti';
    const colors = ['#ffc93c', '#6a5ae0', '#ff8fa3', '#0e7a43', '#4c8dff'];
    const originY = this.#y - this.#dims().h * 0.7;
    for (let i = 0; i < 18; i += 1) {
      const piece = document.createElement('span');
      piece.style.cssText = [
        `left:${this.#x}px`,
        `top:${originY}px`,
        `background:${colors[i % colors.length]}`,
        `--dx:${Math.round((Math.random() * 2 - 1) * 130)}px`,
        `--dy:${Math.round(-50 - Math.random() * 140)}px`,
        `--r:${Math.round(Math.random() * 540)}deg`,
        `animation-delay:${Math.round(Math.random() * 90)}ms`,
      ].join(';');
      box.append(piece);
    }
    document.body.append(box);
    window.setTimeout(() => box.remove(), 1800);
  }

  /* ------------------------------------------------------ comings & goings */

  #showNapButton() {
    this.classList.add('is-napping');
    if (this.#napButton) this.#napButton.hidden = false;
    const dock = this.#dock();
    this.#place(dock.x, dock.y, 1);
  }

  async #appear({ greet = true } = {}) {
    if (this.#state !== 'home' || this.#busy) return;

    if (this.#napping) {
      // Asleep: the sleeping moon slides in at the edge, and the moon holds her seat.
      this.#state = 'out';
      this.#showNapButton();
      this.hidden = false;
      const seat = this.#seat(true);
      if (seat) seat.dataset.away = '';
      return;
    }

    this.#busy = true;
    this.#state = 'out';
    try {
      const dock = this.#dock();
      const seat = this.#seat();
      this.hidden = false;
      if (seat) {
        const rect = seat.getBoundingClientRect();
        this.#place(rect.left + rect.width / 2, rect.bottom, rect.width / this.#dims().w);
        seat.dataset.away = '';
      } else {
        this.#place(-this.#dims().w, dock.y, 1);
      }
      await nextFrame();
      this.#play('boing');
      await this.#flyTo(dock.x, dock.y, { duration: seat ? 820 : 500, arc: seat ? 36 : 40 });
      this.classList.add('is-docked');
      this.#checkTuck();
      let greeted = false;
      if (greet && !this.#done.has('hello')) {
        this.#markDone('hello');
        greeted = true;
        const night = document.documentElement.hasAttribute('data-luna-night');
        this.#say(night ? this.#messages.night : this.#messages.hello, { ms: 3400 });
      }
      // A product moment (or the bottom of the page) may already be on screen; give the
      // hello time to be read first.
      window.setTimeout(() => {
        this.#checkMoments();
        this.#checkBottom();
      }, greeted ? 3800 : 900);
    } finally {
      this.#busy = false;
    }
    this.#settle();
  }

  async #goHome() {
    if (this.#state !== 'out' || this.#busy) return;

    if (this.#napping) {
      // Back in the logo, still asleep.
      this.#hush();
      this.hidden = true;
      this.#state = 'home';
      delete this.#seat(true)?.dataset.away;
      return;
    }

    this.#busy = true;
    try {
      this.#hush();
      const seat = this.#seat();
      if (seat) {
        const rect = seat.getBoundingClientRect();
        this.#play('hop');
        await this.#flyTo(rect.left + rect.width / 2, rect.bottom, {
          duration: 720,
          arc: 50,
          scale: rect.width / this.#dims().w,
        });
      } else {
        await this.#flyTo(-this.#dims().w, this.#y, { duration: 380, arc: 20 });
      }
      delete this.#seat(true)?.dataset.away;
      this.hidden = true;
      this.#state = 'home';
    } finally {
      this.#busy = false;
    }
    this.#settle();
  }

  async #returnToDock() {
    const dock = this.#dock();
    const { w } = this.#dims();
    this.#face(1);
    if (this.#x > window.innerWidth) this.#place(-w, dock.y, 1);
    await this.#flyTo(dock.x, dock.y, { duration: 560, arc: 36, scale: 1 });
    this.classList.add('is-docked');
  }

  /** Puts Luna where she belongs for the current scroll position. */
  #settle() {
    window.clearTimeout(this.#settleTimer);
    if (this.#busy) return;
    const y = window.scrollY;
    if (this.#state === 'home' && y > APPEAR_AT) {
      this.#appear();
    } else if (this.#state === 'out' && y < HOME_AT && (this.#napping || this.#config.template !== '404')) {
      const left = this.#stayUntil - Date.now();
      if (left > 0) {
        this.#settleTimer = window.setTimeout(() => this.#settle(), left + 50);
      } else {
        this.#goHome();
      }
    }
  }

  /* -------------------------------------------------------------- scrolling */

  #onScroll = () => {
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#scrollFrame);
    window.clearTimeout(this.#scrollEndTimer);
    this.#scrollEndTimer = window.setTimeout(this.#scrollEnd, 180);
  };

  #scrollFrame = () => {
    this.#frame = 0;
    const y = window.scrollY;
    const velocity = y - this.#lastScrollY;
    this.#lastScrollY = y;

    if (this.#state === 'out' && !this.#busy && this.#sprite) {
      if (!this.#napping) {
        this.#sprite.style.setProperty('--lean', `${clamp(velocity * 0.5, -12, 12)}deg`);
        this.#sprite.style.setProperty('--lag', `${clamp(-velocity * 0.4, -14, 14)}px`);
      }
      const dock = this.#dock();
      if (Math.abs(dock.y - this.#y) > 1 || Math.abs(dock.x - this.#x) > 1) this.#place(dock.x, dock.y);
    }
    this.#settle();
  };

  #scrollEnd = () => {
    this.#sprite?.style.removeProperty('--lean');
    this.#sprite?.style.removeProperty('--lag');
    if (this.#state !== 'out' || this.#busy || this.#napping) return;
    if (Math.random() < 0.3) this.#play('hop');
    this.#checkTuck();
    this.#checkMoments();
    this.#checkBottom();
  };

  #onResize = () => {
    if (this.#state === 'out' && !this.#busy) {
      const dock = this.#dock();
      this.#place(dock.x, dock.y);
    }
  };

  /**
   * At the bottom of a product page Luna points up: "Ready to order? Tap me."
   * Elsewhere she just gives a little hop. Once per visit each.
   */
  #checkBottom() {
    if (this.#busy || this.#state !== 'out' || this.#napping) return;
    if (this.#bubble && !this.#bubble.hidden) return;
    const footer = document.querySelector('footer');
    if (!footer) return;
    const nearEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 40;
    if (!nearEnd && footer.getBoundingClientRect().top > window.innerHeight * 0.6) return;

    if (this.#config.template === 'product' && this.#buyButton()) {
      if (this.#done.has('ready')) return;
      this.#markDone('ready');
      this.#readyOn = true;
      this.#pose('pointup');
      this.#play('pointup');
      this.#stayUntil = Date.now() + READY_MS;
      this.#say(this.#messages.ready, { ms: READY_MS, action: true });
      return;
    }

    if (this.#done.has('bottom')) return;
    this.#markDone('bottom');
    this.#play('hop');
  }

  /** "Ready to order?" was tapped: back to the Buy it now button, and a hop on it. */
  async #goToBuy() {
    if (!this.#readyOn || this.#busy) return;
    this.#hush();
    const button = this.#buyButton();
    if (!button) return;

    this.#busy = true;
    this.classList.add('is-busy');
    button.scrollIntoView({ behavior: 'smooth', block: 'center' });
    await wait(450);
    let last = -1;
    for (let i = 0; i < 20 && window.scrollY !== last; i += 1) {
      last = window.scrollY;
      await wait(120);
    }
    const dock = this.#dock();
    this.#place(dock.x, dock.y);
    this.classList.remove('is-busy');
    this.#busy = false;

    button.classList.add('luna-glow');
    window.setTimeout(() => button.classList.remove('luna-glow'), 3700);
    this.#lastStunt = 0;
    this.#stunt('buynow', () => this.#hopOnButton(button));
  }

  /* -------------------------------------------------------- product moments */

  #bestLabel() {
    const id = this.#config.bestVariantId;
    if (!id) return null;
    return document.querySelector(`input[data-variant-id="${id}"]`)?.closest('label') || null;
  }

  #buyButton() {
    const forms = [...document.querySelectorAll('product-form-component')].filter(
      (form) => !form.closest('sticky-add-to-cart')
    );
    for (const form of forms) {
      const button = form.querySelector('.shopify-payment-button') || form.querySelector('.add-to-cart-button');
      if (button && button.getBoundingClientRect().height > 0) return button;
    }
    return null;
  }

  #moments() {
    return [
      { name: 'best', target: () => this.#bestLabel(), run: (/** @type {Element} */ el) => this.#pointAtBest(el) },
      {
        name: 'buynow',
        target: () => this.#buyButton(),
        run: (/** @type {Element} */ el) => this.#hopOnButton(el),
        // She lands on top of the button, so it must sit well below the header.
        headroom: this.#dims().h * 0.7 + 6,
      },
      {
        name: 'addon',
        target: () => document.querySelector('.luna-offer__addon'),
        run: (/** @type {Element} */ el) => this.#peekAtAddon(el),
      },
    ];
  }

  #checkMoments() {
    if (this.#config.template !== 'product' || this.#busy || this.#state !== 'out' || this.#napping) return;
    if (performance.now() < FIRST_MOMENT_DELAY_MS) return;
    if (Date.now() - this.#lastStunt < COOLDOWN_MS) return;
    // Never cut off something she is saying (hello, cart signs, Ready to order).
    if (this.#bubble && !this.#bubble.hidden) return;
    for (const moment of this.#moments()) {
      if (this.#done.has(moment.name)) continue;
      const target = moment.target();
      if (target && this.#fullyVisible(target, moment.headroom || 0)) {
        this.#stunt(moment.name, () => moment.run(target));
        return;
      }
    }
  }

  /**
   * Runs a move away from the corner. Luna says her line only once she is back in
   * her corner, so the speech bubble never sits on top of prices, options or buttons.
   * @param {string} name
   * @param {() => Promise<string | undefined>} run Resolves to the line to say afterwards.
   */
  async #stunt(name, run) {
    if (this.#busy || this.#state !== 'out' || this.#napping) return;
    this.#busy = true;
    this.classList.add('is-busy');
    this.#markDone(name);
    this.#hush();
    /** @type {string | undefined} */
    let line;
    try {
      line = await run();
    } finally {
      this.#unpin();
      await this.#returnToDock();
      this.#lastStunt = Date.now();
      this.classList.remove('is-busy');
      this.#busy = false;
    }
    if (line && this.#state === 'out') {
      this.#stayUntil = Date.now() + 3200;
      this.#say(line, { ms: 3200 });
    } else {
      this.#checkTuck();
    }
    this.#settle();
  }

  /**
   * Where Luna stands to peek in from the right edge, level with the element.
   * @param {Element} element
   */
  #peekPoint(element) {
    const { w, h } = this.#dims();
    const rect = element.getBoundingClientRect();
    if (rect.bottom < this.#topInset() || rect.top > window.innerHeight) return null;
    // About 60% of Luna shows; she overlaps only the empty right edge of the row.
    return { x: window.innerWidth - w * 0.12, y: rect.top + rect.height / 2 + h * 0.42 };
  }

  /**
   * Slides in from the right edge facing left and stays level with the element.
   * @param {Element} element
   */
  async #peekIn(element) {
    const start = this.#peekPoint(element);
    if (!start) return false;
    this.#face(-1);
    await this.#flyTo(window.innerWidth + this.#dims().w, start.y, { duration: 480, arc: 40 });
    await this.#flyTo(start.x, start.y, { duration: 260, arc: 0 });
    this.#pinTo(() => this.#peekPoint(element));
    return true;
  }

  async #peekOut() {
    this.#unpin();
    await this.#flyTo(window.innerWidth + this.#dims().w, this.#y, { duration: 240, arc: 0 });
  }

  /** @param {Element} label */
  async #pointAtBest(label) {
    if (!(await this.#peekIn(label))) return undefined;
    label.classList.add('luna-spotlight');
    this.#play('point');
    await wait(2600);
    label.classList.remove('luna-spotlight');
    await this.#peekOut();
    const pack = label.querySelector('input')?.value || label.textContent?.trim() || '';
    return (this.#messages.best || '').replace('{pack}', pack);
  }

  /** @param {Element} card */
  async #peekAtAddon(card) {
    if (!(await this.#peekIn(card))) return undefined;
    this.#play('wave');
    await wait(2200);
    await this.#peekOut();
    return this.#messages.addon;
  }

  /**
   * A quick bounce on the top edge of the button, at its right end (away from the
   * button text), then back to her corner.
   * @param {Element} button
   */
  async #hopOnButton(button) {
    const scale = 0.7;
    const point = () => {
      const rect = button.getBoundingClientRect();
      return { x: rect.right - (this.#dims().w * scale) / 2 - 4, y: rect.top + 4 };
    };
    this.#face(-1);
    this.#play('boing');
    const target = point();
    await this.#flyTo(target.x, target.y, { duration: 720, arc: 90, scale });
    this.#pinTo(point);
    this.#play('cheer');
    await wait(1400);
    return this.#messages.buynow;
  }

  #onBiggerSaving() {
    if (this.#done.has('savings') || this.#napping) return;
    if (this.#state === 'home') {
      // Luna is still in the logo: she sparkles from there.
      /** @type {any} */ (this.#seat())?.play?.('sparkle', { interrupt: true });
      this.#markDone('savings');
      return;
    }
    if (this.#state !== 'out' || this.#busy) return;
    this.#markDone('savings');
    this.#play('sparkle');
    this.#say(this.#messages.savings, { ms: 2600 });
  }

  /* --------------------------------------------------------------- the cart */

  /** @param {Event & { promise?: Promise<any> }} event */
  #onCartUpdate = (event) => {
    if (!event.promise) return;
    event.promise
      .then(async (result) => {
        const cart = result?.cart;
        let count = Number(cart?.totalQuantity ?? cart?.item_count ?? NaN);
        if (Number.isNaN(count)) {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
            headers: { Accept: 'application/json' },
          });
          count = Number((await response.json())?.item_count ?? NaN);
        }
        if (Number.isNaN(count)) return;
        const previous = this.#cartCount;
        this.#cartCount = count;
        if (count > previous) this.#celebrate(count);
      })
      .catch(() => {});
  };

  /** @param {number} count */
  async #celebrate(count) {
    if (this.#napping) return;
    const started = Date.now();
    while ((this.#busy || document.querySelector('dialog[open]')) && Date.now() - started < 20000) {
      await wait(300);
    }
    if (this.#napping || this.#busy) return;

    this.#busy = true;
    this.classList.add('is-busy');
    this.#hush();
    try {
      const { w } = this.#dims();
      if (this.#state === 'home') {
        const seat = this.#seat();
        this.#state = 'out';
        this.hidden = false;
        if (seat) {
          const rect = seat.getBoundingClientRect();
          this.#place(rect.left + rect.width / 2, rect.bottom, rect.width / w);
          seat.dataset.away = '';
        } else {
          const dock = this.#dock();
          this.#place(dock.x, dock.y, 1);
        }
        await nextFrame();
      }

      /** @type {HTMLElement | null} */
      const icon = document.querySelector('header cart-icon') || document.querySelector('cart-icon');
      const rect = icon?.getBoundingClientRect();
      const dock = this.#dock();
      if (icon && rect && rect.bottom > 0 && rect.top < window.innerHeight) {
        this.classList.add('is-front');
        this.#face(1);
        this.#play('boing');
        await this.#flyTo(rect.left + rect.width / 2, rect.top + rect.height * 0.75, {
          duration: 700,
          arc: 80,
          scale: 0.45,
        });
        await this.#flyTo(rect.left + rect.width / 2, rect.top + rect.height * 0.6, {
          duration: 160,
          arc: 0,
          scale: 0.05,
        });
        icon.classList.remove('luna-bump');
        void icon.offsetWidth;
        icon.classList.add('luna-bump');
        await wait(420);
        this.classList.remove('is-front');
        this.#place(dock.x, dock.y, 0.05);
      }
      this.#face(1);
      await this.#flyTo(dock.x, dock.y, { duration: 360, arc: 0, scale: 1 });
      this.classList.add('is-docked');

      if (count >= 2) {
        this.#play('dance');
        this.#confetti();
        this.#say(this.#messages.unlocked, { ms: 4200, sign: true });
      } else {
        this.#play('cheer');
        this.#say(this.#messages.oneMore, { ms: 4200, sign: true });
      }
      this.#stayUntil = Date.now() + 8000;
      await wait(1500);
    } finally {
      this.classList.remove('is-busy');
      this.#busy = false;
      this.#settle();
    }
  }

  /* --------------------------------------------------------- idle and fun */

  #resetIdle = () => {
    window.clearTimeout(this.#idleTimer);
    if (this.#done.has('idle') || this.#napping) return;
    if (!['index', 'product', 'collection'].includes(this.#config.template)) return;
    const seconds = Number(this.#config.idleSeconds) || 25;
    this.#idleTimer = window.setTimeout(this.#onIdle, seconds * 1000);
  };

  /** The shopper went quiet: a quiet stretch, or (if switched on) an offer of help. */
  #onIdle = async () => {
    if (this.#done.has('idle') || this.#napping) return;
    if (this.#busy || document.querySelector('dialog[open]')) {
      this.#resetIdle();
      return;
    }
    this.#markDone('idle');

    if (!this.#config.idleHelp) {
      if (this.#state === 'out') this.#play('stretch');
      return;
    }

    // Set before she appears, so she does not go straight back to the logo at the top.
    this.#stayUntil = Date.now() + 12000;
    if (this.#state === 'home') await this.#appear({ greet: false });
    if (this.#state !== 'out') return;
    this.#stayUntil = Math.max(this.#stayUntil, Date.now() + 9500);
    this.#play('stretch');
    await wait(1500);
    if (this.#state !== 'out' || this.#busy) return;
    this.#say(this.#messages.idle, {
      ms: 8000,
      link: { href: this.#config.helpUrl, label: this.#messages.idleLink },
    });
  };

  #onTap = () => {
    if (this.#busy) return;
    if (this.#readyOn) {
      this.#goToBuy();
      return;
    }
    const now = Date.now();
    this.#taps = this.#taps.filter((time) => now - time < 1300);
    this.#taps.push(now);
    if (this.#taps.length < 3) return;
    this.#taps = [];
    this.#secretDance();
  };

  /** @param {Event} event */
  #onBubbleTap = (event) => {
    if (!this.#readyOn || (event.target instanceof Element && event.target.closest('a'))) return;
    this.#goToBuy();
  };

  async #secretDance() {
    this.#play('dance');
    this.#confetti();
    this.#say(this.#messages.secret, { ms: 2600 });
    await wait(1900);
    this.#play('spin');
  }

  async #lost() {
    this.#stayUntil = Number.POSITIVE_INFINITY;
    await this.#appear({ greet: false });
    this.#play('look');
    this.#say(this.#messages.lost, {
      ms: 15000,
      link: { href: this.#config.homeUrl || '/', label: this.#messages.lostLink },
    });
  }

  /* ------------------------------------------------------------ nap & wake */

  /** The ✕: Luna naps for a few days for this shopper, as a small moon at the edge. */
  #onClose = async (/** @type {Event} */ event) => {
    event.stopPropagation();
    if (this.#busy || this.#napping) return;
    store.set(localStorage, NAP_KEY, String(Date.now() + NAP_DAYS * 24 * 60 * 60 * 1000));
    this.#busy = true;
    this.classList.add('is-busy');
    this.#hush();
    this.#pose(null);
    this.#play('sleepy');
    await wait(900);
    this.#napping = true;
    this.#seatNap(true);
    window.clearTimeout(this.#idleTimer);
    this.#showNapButton();
    this.classList.remove('is-busy');
    this.#busy = false;
    this.#stayUntil = 0;
    this.#say(this.#messages.nap, { ms: 3800 });
  };

  /** A tap on the sleeping moon wakes her up. */
  #onWake = async (/** @type {Event} */ event) => {
    event.stopPropagation();
    if (!this.#napping || this.#busy) return;
    store.set(localStorage, NAP_KEY, '0');
    this.#hush();
    this.#napping = false;
    this.#seatNap(false);
    this.#busy = true;
    this.classList.add('is-busy');
    this.classList.remove('is-napping');
    if (this.#napButton) this.#napButton.hidden = true;
    const dock = this.#dock();
    this.#face(1);
    this.#place(-this.#dims().w, dock.y, 1);
    await nextFrame();
    this.#play('stretch');
    await this.#flyTo(dock.x, dock.y, { duration: 560, arc: 34 });
    this.classList.add('is-docked');
    this.classList.remove('is-busy');
    this.#busy = false;
    this.#say(this.#messages.wake, { ms: 2600 });
  };
}

if (!customElements.get('luna-buddy')) {
  customElements.define('luna-buddy', LunaBuddy);
}
